import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../shared/listView/ListTable';
import { preferencesGroupsStorage } from '../../services/preferencesGroupsStorage';
import { preferencesGroupsService } from '../../services/preferencesGroupsService';
import Checkbox from '../Checkbox';
import useWindowDimensions from '../../hooks/useWindowDimension';

type GroupRow = {
    id: string;
    activityPeriods?: Array<{ dateStart?: string; dateStop?: string }>;
};

type PreferenceGroupsTableProps = {
    items: GroupRow[];
};

const formatDateValue = (date?: string) => {
    if (date) {
        return date.split('T')[0].split('-').slice(1).reverse().join('.');
    }
    return '';
};

const GroupCheckbox = ({ groupId }: { groupId: string }) => {
    const [isChecked, setIsChecked] = useState(
        () => preferencesGroupsStorage.getSelectedList().some((it) => it.id === groupId),
    );

    useEffect(() => {
        const sync = () => {
            setIsChecked(preferencesGroupsStorage.getSelectedList().some((it) => it.id === groupId));
        };
        preferencesGroupsStorage.registerFiltersChangingObserver(sync);
        sync();
        return () => preferencesGroupsStorage.unRegisterFiltersChangingObserver(sync);
    }, [groupId]);

    return (
        <Checkbox
            checked={isChecked}
            handleChecked={() => preferencesGroupsStorage.changeSelectedPreferenceGroups(groupId)}
        />
    );
};

const PreferenceGroupsTable = ({ items }: PreferenceGroupsTableProps) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const [, setRevision] = useState(0);

    useEffect(() => {
        const onChange = () => setRevision((value) => value + 1);
        preferencesGroupsStorage.registerFiltersChangingObserver(onChange);
        return () => preferencesGroupsStorage.unRegisterFiltersChangingObserver(onChange);
    }, []);

    const columns = useMemo<ListTableColumn<GroupRow>[]>(() => [
        {
            id: 'check',
            header: '',
            className: 'list-table__td_check',
            cell: (row) => (
                <ListTableActions>
                    <GroupCheckbox groupId={row.id} />
                </ListTableActions>
            ),
        },
        {
            id: 'name',
            header: i18n.t('app.page.listView.name'),
            cell: (row) => (
                <span className="list-table__name">
                    {preferencesGroupsService.getLocalization(i18n.resolvedLanguage, row, false)}
                </span>
            ),
        },
        {
            id: 'activity',
            header: i18n.t('app.page.preference.groups.activityPeriods'),
            cell: (row) => (
                row.activityPeriods?.length
                    ? row.activityPeriods.map((it, index) => (
                        <div key={`${it.dateStart}-${it.dateStop}-${index}`}>
                            {formatDateValue(it.dateStart)} - {formatDateValue(it.dateStop)}
                        </div>
                    ))
                    : i18n.t('app.page.preference.groups.always')
            ),
        },
    ], [i18n]);

    return (
        <ListTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            width={width}
            isRowSelected={(row) => preferencesGroupsStorage.getSelectedList().some((it) => it.id === row.id)}
            onRowClick={(row) => navigate(`/preference/group/${row.id}`)}
        />
    );
};

export default PreferenceGroupsTable;
